/**
 * Contract for the read-only per-entity event-history endpoint (Normal Core
 * §6.5.5, #845).
 *
 * The HTTP data path the client-side history UI consumes: the EventTimeline
 * (and the AsOfViewer's "as of <t>" history bound) mount in the task drawer
 * first, then commitment/lead/person/goal details. Those blocks are client
 * components, and the no-`fetch()` rail means they reach an aggregate's
 * hash-chained event stream through this contract via `useApi`/`apiCall` — never
 * a bare fetch. Server components can call the core `eventsFor` helper directly;
 * this exists for the client surfaces.
 *
 * Reads only. Events are appended by the engine (`emit`), never via HTTP — the
 * write side is the aggregates + the `*_evented` core helpers, keeping the
 * event discipline in packages/core/src/events. This endpoint returns rows
 * already mapped to the block's `EventTimelineRow` shape: the route resolves
 * actor uids to display names, stamps `isCritical` from the catalog, and passes
 * `occurredAt` through as the stored ISO string (the block formats it via
 * `formatRelativeTime` — no raw SQLite date is composed here). The block never
 * sees a raw `EventEnvelope`, so it stays pure presentation.
 *
 * Entity-scoped: `kind` + `uid` identify exactly one aggregate (its ULID is
 * globally unique); `asOf` bounds the stream for the AsOfViewer. The route
 * additionally filters the returned events to the resolved workspace — a
 * defensive tenant-scope guard so a cross-workspace uid can never surface
 * another tenant's history.
 *
 * Inert until the Phase-6 flip mounts a caller (EventTimeline/AsOfViewer in the
 * task drawer) — nothing fetches this yet, so adding it changes no live
 * behavior (the atomic-flip discipline: build the substrate the flip consumes,
 * mount it at flip time).
 */

import { z } from "zod";
import type { Operation } from "../types";

/**
 * One history row, mirroring the `EventTimelineRow` shape the EventTimeline
 * block consumes (packages/ui/src/blocks/event-timeline.tsx). The route composes
 * these from `EventEnvelope`s server-side.
 */
export const eventTimelineRowSchema = z.object({
  uid: z.string(),
  /** "<aggregate>:<verb>" — the catalog type, rendered as the row's pill. */
  type: z.string(),
  /** Pre-resolved actor display name; null = system. */
  actorLabel: z.string().nullable(),
  /** Stored ISO timestamp; the block formats it (never a raw composed date). */
  occurredAt: z.string(),
  /** One-line payload summary, composed server-side; null falls back to type. */
  summary: z.string().nullable(),
  /** Upstream cause uid; powers the "caused by…" affordance. */
  causationUid: z.string().nullable(),
  /** High-stakes events (status/money/identity) get the warning pill. */
  isCritical: z.boolean(),
});
export type EventTimelineRowDto = z.infer<typeof eventTimelineRowSchema>;

/**
 * Entity scope (kind + uid required) + optional history bound / cap. Mirrors the
 * core `eventsFor` options (`upToTime`) plus a client-facing `limit`.
 */
export const eventsListQuery = z.object({
  /** Aggregate kind, e.g. "task" | "person" | "commitment". */
  kind: z.string().min(1),
  /** Aggregate uid (the entity's ULID). */
  uid: z.string().min(1),
  /** ISO time; bounds the stream to events at-or-before it (AsOfViewer). */
  asOf: z.string().optional(),
  /** Newest-first cap. */
  limit: z.coerce.number().int().min(1).max(500).optional(),
});

export const eventsList: Operation<
  void,
  z.infer<typeof eventsListQuery>,
  void,
  { events: EventTimelineRowDto[] }
> = {
  method: "GET",
  path: () => "/api/events",
  query: eventsListQuery,
  response: z.object({ events: z.array(eventTimelineRowSchema) }),
};

/**
 * The causal-chain query (Normal Core §6.5.8, Phase 8) — the "Why?" affordance.
 * Given any event uid, the route walks its `causation_uid` ancestry (the core
 * `causalChain` helper) and returns the chain ROOT-CAUSE FIRST … → this event,
 * mapped to the same `EventTimelineRow` shape the CausalTraceView renders. This
 * is how a Play card / attention item / agent action answers "why am I being
 * shown this": signup → assignment → recommendation → action.
 */
export const eventsChainQuery = z.object({
  /** The event uid whose causal ancestry to walk (the "this happened" anchor). */
  uid: z.string().min(1),
  /**
   * Optional anchor aggregate kind. When provided, the route also folds in the
   * anchor aggregate's OWN lifecycle history (created → updated → state_changed →
   * …) ahead of the bare causation chain, so a recommendation derived from a
   * commitment's event log renders the full narrative — a ≥3-link trace (§12-8)
   * — even when those lifecycle events are seq-linked rather than chained via
   * `causation_uid`. Omitted ⇒ the pure `causalChain` ancestry (unchanged).
   */
  kind: z.string().min(1).optional(),
});

export const eventsChain: Operation<
  void,
  z.infer<typeof eventsChainQuery>,
  void,
  { chain: EventTimelineRowDto[] }
> = {
  method: "GET",
  path: () => "/api/events/chain",
  query: eventsChainQuery,
  response: z.object({ chain: z.array(eventTimelineRowSchema) }),
};

export const events = {
  list: eventsList,
  chain: eventsChain,
};
